import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    HeaderTitleBox,
    HeaderWrapper,
    StatisticsBox,
    StatisticsHeader,
    StyledCard,
    StyledContent,
    StyledPage,
    StyledSubtitle
} from "./Statistics.styled";
import {Button, Grid, Stack, Typography} from "@mui/material";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import {LocalDocumentStatisticsResponse} from "../../../models/local-documents.models";
import {localDocumentsService} from "../../../services/local-documents.service";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import CircularProgress from "@mui/material/CircularProgress";
import i18n from "i18next";
import {env} from "../../../config/environment";

const initData = {
    documentNumber: 0,
    patientNumber: 0,
    hisExportedDocumentsNumber: 0,
    archivedDocumentsNumber: 0,
    categoryDocumentsMap: {},
};

const Statistics = () => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();

    const [isFetching, setIsFetching] = useState(false);
    const [isError, setIsError] = useState(false);

    const [data, setData] = useState<LocalDocumentStatisticsResponse>(initData);

    const { clientTheme } = env;
    const systemName = t(`brand.${clientTheme}.productName`);

    const fetchData = useCallback(async () => {
        try {
            setIsFetching(true);
            const response = await localDocumentsService.fetchStatistics();
            setData(response);
        } catch (error) {
            console.error('Error fetching data:', error);
            showSnackbar(t('statistics.fetchDataError'), "error");
            setIsError(true);
        } finally {
            setIsFetching(false);
        }
    }, [t]);

    useEffect(() => {
        void fetchData();
    }, []);

    const handleExportToPdf = async () => {
        const lang = (i18n.language || 'pl').split('-')[0];

        try {
            const binaryData = await localDocumentsService.exportStatisticsToPdf(lang);
            const blob = new Blob([binaryData], { type: 'application/pdf' });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = 'statistics.pdf';

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showSnackbar(t('statistics.exportSuccess'), "success");
        } catch (error) {
            console.error('Error exporting data:', error);
            showSnackbar(t('statistics.exportError'), "error");
        }
    };

    const statistics = useMemo(() => ([
        {
            icon: <DescriptionOutlinedIcon color="secondary" />,
            label: t('statistics.documentsInfo.label'),
            hint: t('statistics.documentsInfo.hint'),
            value: data.documentNumber,
        },
        {
            icon: <PeopleAltOutlinedIcon color="secondary" />,
            label: t('statistics.patientsInfo.label'),
            hint: t('statistics.patientsInfo.hint'),
            value: data.patientNumber,
        },
        {
            icon: <DriveFolderUploadOutlinedIcon color="secondary" />,
            label: t('statistics.exportedDocumentsInfo.label'),
            hint: t('statistics.exportedDocumentsInfo.hint'),
            value: data.hisExportedDocumentsNumber,
        },
        {
            icon: <ArchiveOutlinedIcon color="secondary" />,
            label: t('statistics.documentsArchivedInfo.label'),
            hint: t('statistics.documentsArchivedInfo.hint'),
            value: data.archivedDocumentsNumber,
        },
    ]), [t, data]);

    return (
        <StyledPage role="region" aria-labelledby="statistics-title">
            <StyledCard>
                <HeaderWrapper>
                    <HeaderTitleBox>
                        <Typography id="statistics-title" variant="h4" component="h2">
                            {t('statistics.title')}
                        </Typography>
                        <Typography variant="body2" component="span" color="secondary.light">
                            {t('statistics.subtitle', {systemName})}
                        </Typography>
                    </HeaderTitleBox>

                    <Button variant="contained"
                            color="primary"
                            onClick={handleExportToPdf}
                            disabled={isFetching || isError}
                            sx={{ alignSelf: 'center' }}
                    >
                        {t('statistics.exportButton')}
                    </Button>
                </HeaderWrapper>

                <StyledContent>
                    {isFetching && (
                        <CircularProgress size={64} />
                    )}

                    {isError && (
                        <Stack direction="column" alignItems={"center"}>
                            <Typography variant="body2" component="p" color="secondary.light">
                                {t('statistics.noData')}
                            </Typography>
                        </Stack>
                    )}

                    {(!isFetching && !isError) && (
                        <Stack direction="column" spacing={5} alignItems={"center"}>
                            <Grid container spacing={0} columnSpacing={{ xs: 0, lg: 2.5 }} justifyContent="center" sx={{ rowGap: 5 }}>
                                {statistics.map((datum, index) => (
                                    <Grid key={index} item xs={12} lg={6}
                                          sx={{
                                              "display": 'flex',
                                              "alignItems": 'center',
                                              "justifyContent": { "xs": 'center', "lg": index % 2 === 0 ? 'flex-end' : 'flex-start' },
                                              "pt": { "xs": 0 },
                                              "pl": { "xs": 0 },
                                              "pr": { "lg": 2.5 },
                                          }}
                                    >
                                        <StatisticsBox>
                                            <StatisticsHeader>
                                                {datum.icon}

                                                <StyledSubtitle>
                                                    {datum.label}
                                                </StyledSubtitle>
                                            </StatisticsHeader>

                                            <Typography variant="h3" component="p">
                                                {datum.value}
                                            </Typography>

                                            <Typography variant="body2" component="p" color="secondary.light">
                                                {datum.hint}
                                            </Typography>
                                        </StatisticsBox>
                                    </Grid>
                                ))}
                            </Grid>

                            <StatisticsBox>
                                <StatisticsHeader>
                                    <GroupWorkOutlinedIcon color="secondary" />

                                    <StyledSubtitle>
                                        {t('statistics.categoryDocumentsInfo')}
                                    </StyledSubtitle>
                                </StatisticsHeader>

                                <Stack direction="column" spacing={2}>
                                    {Object.entries(data.categoryDocumentsMap).length === 0 &&
										<Typography variant="body2" component="p" color="secondary.light">
                                            {t('statistics.noData')}
                                        </Typography>
                                    }

                                    {Object.entries(data.categoryDocumentsMap).length > 0 &&
                                        Object.entries(data.categoryDocumentsMap).map(([category, count]) => (
                                            <Stack key={category} direction="row" justifyContent="space-between">
                                                <Typography variant="body1" component="p" pl={4} flex={3}>
                                                    {category}
                                                </Typography>

                                                <Typography variant="body1" component="p" pr={4} flex={1} textAlign={"right"} color="secondary">
                                                    {count}
                                                </Typography>
                                            </Stack>
                                    ))}
                                </Stack>

                            </StatisticsBox>
                        </Stack>
                    )}
                </StyledContent>
            </StyledCard>
        </StyledPage>
    );
};

export default Statistics;